/**
 * Hook for tracking pageviews in PostHog.
 *
 * OWNERSHIP: this module is the single owner of $pageview capture. The
 * posthog-js automatic capture (initial pageload) is disabled in
 * PostHogManager.initializeSync — with both enabled, every boot emitted the
 * SDK's absolute-URL pageview alongside this module's, for the same screen.
 */

import { useEffect } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { isElectronApp } from '#~/electron/utils.js';
import { PostHogManager } from '#~/posthog/manager.js';
import { POSTHOG_ENVELOPE_EVENT_NAMES } from '#~/posthog/event-names.js';
import { mapRouteToPageview } from './route-mapper.js';
import type { CanonicalPageviewPath, PageviewContext, PageviewProperties } from './types.js';

/**
 * Last route captured in THIS document, or null before the first capture.
 *
 * Deliberately module scope, not a `useRef`. `<Trackers>` sits inside the auth
 * `Synchronizer`, which renders `<Loader/>` instead of the app subtree while
 * `isAuthenticated && !hasStudioUser` (auth/synchronizer.tsx). When the studio
 * user resolves, that ternary swaps back and React unmounts and remounts the
 * whole subtree — including this hook. A per-instance ref starts empty on the
 * remount, so the unchanged route captured a second $pageview a millisecond
 * later; that is the double count this owner exists to prevent, and it applied
 * to every screen, not just boot. Document scope survives any remount while
 * still letting a genuine re-visit (navigate away, come back) count again.
 */
let lastRouteKey: string | null = null;

/**
 * Hook to automatically track pageviews when the route changes.
 *
 * This hook listens to React Router location changes and sends
 * $pageview events to PostHog with the appropriate path and context.
 */
export function usePageviewTracker(): void {
  const location = useLocation();
  const params = useParams();

  useEffect(() => {
    // Desktop redirects this web-only route before showing its questions.
    if (isElectronApp() && location.pathname === '/agent/setup/prequal') {
      return;
    }

    // Keyed on pathname ALONE, because the event this hook sends is a pure
    // function of the pathname: mapRouteToPageview reads nothing else, so a
    // change that leaves the pathname alone cannot change the emitted payload
    // and re-emitting it is a duplicate by definition. Including search here
    // (as this guard used to) let a query-only change through, and the tray
    // proves that matters — its location is `/electron/tray?iris_role=tray`
    // while its canonical $pathname is `/electron/tray`, so the extra row was
    // indistinguishable from the first in every dashboard.
    if (lastRouteKey === location.pathname) {
      return;
    }
    lastRouteKey = location.pathname;

    trackPageview(mapRouteToPageview(location.pathname));
  }, [location.pathname, location.search, params]);
}

/**
 * Reset the document-scoped route dedup.
 *
 * Only a test needs this: the key intentionally lives as long as the document,
 * so without a reset one case's capture would suppress the next case's.
 */
export function resetRouteDedupForTests(): void {
  lastRouteKey = null;
}

/**
 * Track a pageview event in PostHog.
 *
 * Single owner of the $pageview URL contract: $current_url is always the
 * full absolute URL (PostHog's definition — its URL transformations reject
 * relative values) and $pathname is the canonical grouped screen path from
 * the caller. Minting $current_url here, after the caller's properties,
 * guarantees no call site can regress the contract with a relative value.
 *
 * @param properties - Pageview properties to track ($pathname required)
 */
export function trackPageview(properties: PageviewProperties): void {
  const posthog = PostHogManager.getInstance();

  // Explicit undefined guard: pageviews only ever fire from a renderer, but
  // non-DOM callers (tests, SSR) must degrade to no-URL rather than throw.
  const absoluteUrl = typeof window === 'undefined' ? undefined : window.location.href;
  const payload: PageviewProperties =
    absoluteUrl === undefined ? properties : { ...properties, $current_url: absoluteUrl };

  try {
    posthog.capture(POSTHOG_ENVELOPE_EVENT_NAMES.PAGEVIEW, payload);
  } catch (error) {
    console.error('[Pageview] Failed to track pageview:', error);
  }
}

/**
 * Hook to track a virtual pageview for a modal, dialog, or workflow panel.
 *
 * Dedup lives in the effect's dependency list, keyed on the CANONICAL path
 * rather than on the caller's panel enum. Several panels legitimately map to
 * one canonical screen (video settings' `Video` and `VideoSettings` both mean
 * `/advanced-settings/video`), and every workflow scaffold used to key its own
 * effect on the panel — so switching between two such panels emitted a second
 * $pageview for a screen the user never left. Keying on the value actually
 * sent is what makes "one open, one pageview" true.
 *
 * Instance scope is correct here, unlike the route tracker above: these
 * scaffolds mount and unmount with their dialog, so unmount means closed and
 * a remount is a genuine reopen that should count again.
 *
 * @param path - Canonical virtual path, or null when no screen is active
 * @param isOpen - Whether the modal/dialog/panel is open
 * @param additionalProps - Additional context properties to include
 */
export function useVirtualPageview(
  path: CanonicalPageviewPath | null,
  isOpen: boolean,
  additionalProps?: PageviewContext
): void {
  useEffect(() => {
    if (!isOpen || path === null) {
      return;
    }

    // Virtual screens group by canonical $pathname; the real absolute URL
    // of the underlying page is minted by trackPageview.
    trackPageview({
      $pathname: path,
      ...additionalProps
    });
    // additionalProps is deliberately absent: it is context ABOUT this screen,
    // not an identity for it, and it is rebuilt fresh on every render — in the
    // deps it would re-fire the same screen on every parent re-render.
  }, [path, isOpen]);
}
